/**
 * @file 红点统计相关
 */

import { formatDate, isEmpty, isNotEmpty, isString } from '@tencent/slug-function';
import { getLocal, setLocal } from '@utils/storage';

interface redPointItem {
  type: 'number' | 'general';
  num: number;
}

interface redPoint {
  [propName: string]: redPointItem;
}

interface LooseObject {
  [key: string]: any;
}

interface redPointLocalParams {
  module: string;
  key: string;
}

/**
 * 统计的红点数值
 * @param data 要统计的红点数据集合
 * @param keys 传入一个数组，包含要被统计的字段名
 *
 * @returns 返回统计后的红点数值
 *
 * @example getRedPointSum(data, ['point', 'ipbd']) // => {type: 'number', num: 10}
 */
export const getRedPointSum = (data: redPoint, keys?: string[]): redPointItem => {
  const redPointSum: redPointItem = {
    type: 'general',
    num: 0,
  };

  if (isEmpty(data)) {
    return redPointSum;
  }

  for (const key in data) {
    // 未指定keys，则全部加上。 指定了keys，则只增加指定字段的值。
    if (
      Object.prototype.hasOwnProperty.call(data, key)
      && (isEmpty(keys) || (keys as string[]).includes(key))
    ) {
      const { type, num } = data[key];
      // 只要子级有数字类型的，父级也是数字类型
      if (type === 'number') {
        redPointSum.type = 'number';
        redPointSum.num = redPointSum.num + num;
      } else {
        redPointSum.num = redPointSum.num + (num === 0 ? 0 : 1);
      }
    }
  }
  return redPointSum;
};

/**
 * 从 localstorage 获取红点是否被点击过
 * @param params.module 要获取的红点的模块
 * @param params.key 要获取的模块中的字段名
 * @param judgeIsToday 是否需要判断今日点击状态，默认不判断（点击过即可），传true则必须是今天点击过才会返回true
 *
 * @returns 返回红点点击情况
 *
 * @example getRedPointClickLocal({ module: 'welfare', key: 'ipbd'}, true) // => true
 */
export const getRedPointClickLocal = (
  { module, key }: redPointLocalParams,
  judgeIsToday?: boolean,
): boolean => {
  const today = formatDate(new Date(), 'MMdd');
  const data: string | LooseObject = (getLocal('redPoint') as unknown) as LooseObject;
  if (isEmpty(data) || isString(data)) {
    return false;
  }

  try {
    if (judgeIsToday) {
      // 计算是否今天点击过
      return today === data[module][key];
    }
    return isNotEmpty(data[module][key]);
  } catch (error) {
    return false;
  }
};

/**
 * 往 localstorage 中设置红点已被点击的数据
 * @param module 要设置的红点的模块
 * @param key 要设置的模块中的字段名
 *
 * @example setRedPointClickLocal({ module: 'welfare', key: 'ipbd'})
 */
export const setRedPointClickLocal = ({ module, key }: redPointLocalParams): void => {
  const today = formatDate(new Date(), 'MMdd');
  let data: string | LooseObject = (getLocal('redPoint') as unknown) as LooseObject;

  // 给data中赋值
  if (isEmpty(data) || isString(data)) {
    data = {};
    data[module] = {};
    data[module][key] = today;
  }
  if (isEmpty(data[module])) {
    data[module] = {};
  }
  data[module][key] = today;
  setLocal('redPoint', data);
};
